import { ref, computed } from 'vue';

/**
 * Composable for pagination logic
 * Manages page state, calculations, and navigation
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.perPage - Items per page (default: 12)
 * @param {number} options.maxVisible - Maximum visible page numbers (default: 5)
 * @returns {Object} Pagination state and methods
 */
export function usePagination(options = {}) {
  const {
    initialPage = 1,
    perPage: initialPerPage = 300,
    maxVisible = 5
  } = options;

  const currentPage = ref(initialPage);
  const perPage = ref(initialPerPage);
  const totalItems = ref(0);

  /**
   * Calculate total number of pages
   */
  const totalPages = computed(() => {
    return Math.ceil(totalItems.value / perPage.value);
  });

  /**
   * Calculate visible page numbers based on current page
   * Returns array of page numbers to display in pagination
   */
  const visiblePages = computed(() => {
    const pages = [];
    let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages.value, start + maxVisible - 1);

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  });

  /**
   * Check if we're on the first page
   */
  const isFirstPage = computed(() => currentPage.value === 1);

  /**
   * Check if we're on the last page
   */
  const isLastPage = computed(() => currentPage.value === totalPages.value);

  /**
   * Navigate to a specific page
   * @param {number} page - Page number to navigate to
   * @returns {boolean} Whether navigation was successful
   */
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
      currentPage.value = page;
      return true;
    }
    return false;
  };

  /**
   * Navigate to the next page
   */
  const nextPage = () => {
    if (!isLastPage.value) {
      currentPage.value++;
      return true;
    }
    return false;
  };

  /**
   * Navigate to the previous page
   */
  const previousPage = () => {
    if (!isFirstPage.value) {
      currentPage.value--;
      return true;
    }
    return false;
  };

  /**
   * Reset to first page
   */
  const resetPage = () => {
    currentPage.value = 1;
  };

  /**
   * Set total items count (usually from API response)
   * @param {number} total - Total number of items
   */
  const setTotalItems = (total) => {
    totalItems.value = total;
  };

  /**
   * Set items per page
   * @param {number} count - Number of items per page
   */
  const setPerPage = (count) => {
    perPage.value = count;
    // Reset to first page when changing per page
    resetPage();
  };

  /**
   * Get pagination metadata for API calls
   * @returns {Object} Pagination params for API
   */
  const getPaginationParams = () => ({
    page: currentPage.value,
    per_page: perPage.value
  });

  return {
    // State
    currentPage,
    perPage,
    totalItems,
    
    // Computed
    totalPages,
    visiblePages,
    isFirstPage,
    isLastPage,
    
    // Methods
    goToPage,
    nextPage,
    previousPage,
    resetPage,
    setTotalItems,
    setPerPage,
    getPaginationParams
  };
}
